import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, ShieldCheck, Info } from "@phosphor-icons/react";

const CATS = [
  { v: "promotional", l: "Promotional" },
  { v: "informational", l: "Informational" },
  { v: "reminder", l: "Reminder" },
];

const EMPTY = { name: "", content: "", category: "promotional", content_sid: "", extra_var_labels: [] };

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = async () => setItems((await api.get("/templates")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setF({
      name: t.name, content: t.content, category: t.category,
      content_sid: t.content_sid || "",
      extra_var_labels: t.extra_var_labels || [],
    });
    setOpen(true);
  };
  const save = async () => {
    if (!f.name.trim() || !f.content.trim()) return toast.error("Name and preview content are required");
    const payload = {
      ...f,
      content_sid: f.content_sid.trim() || null,
      extra_var_labels: (f.extra_var_labels || []).map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Template deleted"); load();
  };

  const updateLabel = (i, v) => {
    const arr = [...(f.extra_var_labels || [])];
    arr[i] = v;
    setF({ ...f, extra_var_labels: arr });
  };
  const addLabel = () => setF({ ...f, extra_var_labels: [...(f.extra_var_labels || []), ""] });
  const removeLabel = (i) => setF({ ...f, extra_var_labels: f.extra_var_labels.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="templates-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Reusable</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Message templates</h1>
          <p className="text-brand-text/70 mt-1">Write once, send many times. Attach an approved Twilio Content SID for real WhatsApp delivery.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-5 py-3 text-sm font-semibold transition-colors" data-testid="new-template-btn">
          <Plus size={18} weight="bold" /> New template
        </button>
      </div>

      <div className="mb-6 rounded-2xl bg-brand-secondary/10 border border-brand-secondary/25 p-4 flex gap-3" data-testid="tpl-info">
        <Info size={20} weight="duotone" className="text-brand-secondary shrink-0 mt-0.5" />
        <div className="text-sm text-brand-text/80">
          <strong>Real WhatsApp send</strong> requires an approved template from Twilio.
          Create one in <a href="https://console.twilio.com/us1/develop/content-editor" target="_blank" rel="noreferrer" className="text-brand-primary font-semibold hover:underline">Twilio Content Editor</a>,
          submit for approval, then paste the Content SID (starts with <code className="text-xs font-mono bg-white px-1.5 py-0.5 rounded">HX…</code>) into your template here.
          Templates without a Content SID only work as freeform inside a 24-hour session window.
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-brand-border rounded-2xl p-12 text-center text-brand-textMuted">
            No templates yet. Create your first one.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-brand-border rounded-2xl p-5 flex flex-col" data-testid={`tpl-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-primary">{t.category}</div>
              <div className="flex gap-1">
                <button onClick={()=>openEdit(t)} data-testid={`edit-${t.id}`} className="p-2 rounded-lg hover:bg-brand-bg"><PencilSimple size={16} weight="duotone" /></button>
                <button onClick={()=>remove(t.id)} data-testid={`del-tpl-${t.id}`} className="p-2 rounded-lg hover:bg-brand-bg text-brand-error"><Trash size={16} weight="duotone" /></button>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="font-heading text-lg font-bold">{t.name}</div>
              {t.content_sid && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-brand-success/15 text-brand-success px-1.5 py-0.5 rounded font-semibold">
                  <ShieldCheck size={11} weight="fill" /> Approved
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-brand-text/75 whitespace-pre-wrap">{t.content}</div>
            {t.content_sid && <div className="mt-3 pt-3 border-t border-brand-border text-[11px] font-mono text-brand-textMuted truncate">{t.content_sid}</div>}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" data-testid="tpl-modal">
          <div className="bg-white w-full max-w-lg rounded-3xl border border-brand-border p-6 sm:p-8 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl font-bold">{editing ? "Edit template" : "New template"}</h3>
              <button onClick={()=>setOpen(false)} data-testid="close-tpl"><X size={22} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold">Name</label>
                <input value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} data-testid="tpl-name"
                  placeholder="Weekend offer"
                  className="mt-1.5 w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
              </div>
              <div>
                <label className="text-sm font-semibold">Category</label>
                <select value={f.category} onChange={(e)=>setF({...f, category: e.target.value})} data-testid="tpl-cat"
                  className="mt-1.5 w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border">
                  {CATS.map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold">Preview message (uses <code className="text-xs font-mono">{"{{1}}"}</code>=name, <code className="text-xs font-mono">{"{{2}}"}</code>=business, <code className="text-xs font-mono">{"{{3+}}"}</code>=extras)</label>
                <textarea value={f.content} onChange={(e)=>setF({...f, content: e.target.value})} rows={4} data-testid="tpl-content"
                  placeholder="Hi {{1}}, {{2}} has a special offer: {{3}}. Come by this week!"
                  className="mt-1.5 w-full p-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold flex items-center gap-2">
                  Twilio Content SID <span className="text-[10px] uppercase tracking-widest bg-brand-success/15 text-brand-success px-1.5 py-0.5 rounded font-semibold">Optional</span>
                </label>
                <input value={f.content_sid} onChange={(e)=>setF({...f, content_sid: e.target.value})} data-testid="tpl-sid"
                  placeholder="HXfe5ab5f00277942d4d4200328b4d403c"
                  className="mt-1.5 w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none font-mono text-xs" />
                <p className="mt-1 text-xs text-brand-textMuted">From Twilio Console → Content Editor. Required for real WhatsApp delivery.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Extra variables (from <code className="text-xs font-mono">{"{{3}}"}</code> onward)</label>
                  <button type="button" onClick={addLabel} data-testid="add-label" className="text-xs text-brand-primary font-semibold hover:underline">+ Add</button>
                </div>
                <p className="mt-1 mb-2 text-xs text-brand-textMuted">Labels shown to vendor when sending. E.g. "Offer text", "Coupon code".</p>
                {(f.extra_var_labels || []).length === 0 && <div className="text-xs text-brand-textMuted italic">No extra variables. {"{{1}}"} name + {"{{2}}"} business are always auto-filled.</div>}
                {(f.extra_var_labels || []).map((label, i) => (
                  <div key={i} className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-mono text-brand-textMuted w-10">{`{{${i+3}}}`}</span>
                    <input value={label} onChange={(e)=>updateLabel(i, e.target.value)} data-testid={`label-${i}`}
                      placeholder="Label for this variable"
                      className="flex-1 h-10 px-3 rounded-lg bg-brand-bg border border-brand-border text-sm" />
                    <button type="button" onClick={()=>removeLabel(i)} className="text-brand-error"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={()=>setOpen(false)} className="flex-1 h-11 rounded-full border border-brand-border font-semibold" data-testid="cancel-tpl">Cancel</button>
              <button onClick={save} className="flex-1 h-11 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold" data-testid="save-tpl">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
